/**
 * EpSellPage - EP1/EP2 listing (USDT-only)
 *
 * Route: /ep-sell          -> EP1 listing
 *        /ep-sell?type=ep2 -> EP2 listing
 *
 * Unit price comes from active `ep_price_tier.usdt_price` (EP 交易配置).
 * Fiat rails (bank / wechat / alipay) are disabled — USDT BEP20 only.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../api/client';
import { epAPI } from '../api/ep';
import { usersAPI } from '../api/users';
import { configAPI } from '../api/config';
import { useAuthStore } from '../store/auth';
import { Button } from '../components/ui/Button';
import { ThemedPageHeader } from '../components/ui/ThemedPageHeader';
import { ThemedPanel, ThemedInput, ThemedTextarea, ThemedField, ThemedNotice } from '../components/ui/ThemedSurface';
import { TransactionSecurity } from '../components/ui/TransactionSecurity';
import { EPSELL_COLORS as C, COLORS } from '@/constants';
import { EP_TRADE_MIN_ORDER_EP, EP_TRADE_MAX_ORDER_EP } from '@uo/shared';
import { useEpTradeEligible } from '../hooks/useEpTradeEligible';
import { useEpTradingWindow } from '../hooks/useEpTradingWindow';
import { EpTradeLevelGate } from '../components/ep/EpTradeLevelGate';
import { LoadingSkeleton } from '../components/ui/LoadingSkeleton';

type ActiveTier = {
  level: number;
  cny_min: string;
  cny_max: string;
  usdt_price: string;
  usdt_amount: string;
};

function InfoBanner({ epType }: { epType: string }) {
  const { t } = useTranslation();

  return (
    <ThemedPanel className="p-4">
      <p className="text-xs leading-relaxed" style={{ color: C.muted }}>
        <span className="font-semibold" style={{ color: C.gold }}>#{t('epSell.hint')}#</span>{' '}
        1. {t('epTrading.rules1', { epType })}
        <br />
        2. {t('epTrading.rules2')}
        <br />
        3. {t('epTrading.rules3')}
      </p>
    </ThemedPanel>
  );
}

export function EpSellPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const profile = useAuthStore((s) => s.profile);
  const { eligible, loading: accessLoading } = useEpTradeEligible();
  const hallOpen = useEpTradingWindow();

  const isEp2 = searchParams.get('type') === 'ep2';
  const epType = isEp2 ? 'EP2' : 'EP1';
  const epKey = isEp2 ? 'ep2' : 'ep1';

  const [quantity, setQuantity] = useState('');
  const [unitPriceUsdt, setUnitPriceUsdt] = useState('');
  const [paymentNote, setPaymentNote] = useState('');
  const [txPassword, setTxPassword] = useState('');
  const [securityAnswer, setSecurityAnswer] = useState('');
  const [showSecurity, setShowSecurity] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const { data: balData } = useQuery({
    queryKey: ['user', 'balances'],
    queryFn: () => usersAPI.getBalances(),
    refetchInterval: 30_000,
    enabled: eligible,
  });

  const { data: quotaData } = useQuery({
    queryKey: ['ep', 'daily-quota'],
    queryFn: () => epAPI.getDailyQuota(),
    refetchInterval: 60_000,
    enabled: eligible,
  });

  const { data: profileData } = useQuery({
    queryKey: ['user', 'profile'],
    queryFn: () => usersAPI.getProfile() as Promise<{ wallet_address?: string }>,
    enabled: eligible,
  });

  const { data: activeTier, isError: tierError } = useQuery({
    queryKey: ['ep-pricing', 'active'],
    queryFn: async () => {
      const row = await configAPI.getActiveEpPricing();
      return row as unknown as ActiveTier;
    },
    retry: false,
    enabled: eligible,
  });

  // Lock unit price to active tier USDT price.
  useEffect(() => {
    if (activeTier?.usdt_price) {
      setUnitPriceUsdt(activeTier.usdt_price);
    }
  }, [activeTier?.usdt_price]);

  const balance = balData?.balances?.[epKey] ?? balData?.balances?.ep ?? '0';
  const dailyLimitReached = quotaData?.used ?? false;
  const boundWallet = (profileData?.wallet_address ?? '').trim();

  const qtyNum = Number(quantity);
  const priceUsdtNum = Number(unitPriceUsdt);
  const estimatedIncomeUsdt =
    qtyNum > 0 && priceUsdtNum > 0 ? qtyNum * priceUsdtNum : null;
  const listingFeeEp = 5;
  const sellerLockEp =
    qtyNum > 0 && Number.isInteger(qtyNum) ? qtyNum + listingFeeEp : null;

  const sellMut = useMutation({
    mutationFn: () =>
      epAPI.create({
        ep_type: epKey as 'ep1' | 'ep2',
        amount_ep: String(qtyNum),
        // Field name is legacy `price_cny`; for USDT-only listings we store
        // the tier USDT unit price so the hall can display USDT correctly.
        price_cny: String(unitPriceUsdt),
        payment_methods: ['usdt'],
        note: paymentNote.trim() || undefined,
      }),
    onSuccess: () => {
      setSuccess(true);
      setError(null);
      setTimeout(() => navigate(-1), 1500);
    },
    onError: (e: unknown) => {
      setError(e instanceof ApiError ? e.message : t('epSell.submitFailed'));
    },
  });

  function handleSubmit() {
    setError(null);

    if (!hallOpen) {
      setError(t('epSell.windowClosed', '挂卖仅在每日 9:00-11:00、13:30-15:30（北京时间）开放'));
      return;
    }

    if (
      !quantity ||
      qtyNum < EP_TRADE_MIN_ORDER_EP ||
      qtyNum > EP_TRADE_MAX_ORDER_EP ||
      !Number.isInteger(qtyNum)
    ) {
      setError(t('epSell.qtyError'));
      return;
    }
    if (!unitPriceUsdt || priceUsdtNum <= 0) {
      setError(t('epSell.priceError'));
      return;
    }
    if (!boundWallet) {
      setError(t('epSell.usdtWalletRequired'));
      return;
    }
    if (!activeTier?.usdt_price) {
      setError(
        t('epSell.tierMissing', '未匹配到 EP 交易配置档位，请联系管理员检查价格表'),
      );
      return;
    }

    sellMut.mutate();
  }

  const securityQuestion =
    (profile as Record<string, unknown> | null)?.['security_question'] as string | undefined;
  const questionText = securityQuestion ?? t('epSell.defaultSecurityQuestion');

  const formDisabled = dailyLimitReached || sellMut.isPending || success || !hallOpen;
  const requiredFieldsFilled =
    quantity.trim() !== '' && unitPriceUsdt.trim() !== '' && !!boundWallet;

  if (accessLoading || !eligible) {
    return (
      <div className="relative min-h-full pb-8" style={{ background: C.pageBg }}>
        <div className="relative mx-auto min-h-full w-full max-w-[430px]">
          <ThemedPageHeader
            title={isEp2 ? t('epSell.titleEp2', 'EP2 挂卖') : t('epSell.title', 'EP1 挂卖')}
            onBack={() => navigate(-1)}
            theme={C}
          />
          <main className="px-5 pt-20">
            {accessLoading ? <LoadingSkeleton lines={4} /> : <EpTradeLevelGate />}
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-full" style={{ background: C.pageBg }}>
      <div className="relative mx-auto min-h-full w-full max-w-[430px]">
        <ThemedPageHeader
          title={isEp2 ? t('epSell.titleEp2') : t('epSell.title')}
          onBack={() => navigate(-1)}
          theme={C}
        />

        <div className="px-5 pb-5 pt-[70px] flex flex-col gap-5">
          <InfoBanner epType={epType} />
          {!hallOpen && (
            <ThemedNotice tone="warning">
              {t(
                'epSell.windowClosed',
                '挂卖仅在每日 9:00-11:00、13:30-15:30（北京时间）开放',
              )}
            </ThemedNotice>
          )}

          <ThemedPanel className="p-4 flex flex-col gap-4 overflow-visible">
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <span className="text-sm" style={{ color: C.muted }}>
                  {t('common.available')}
                  {epType}
                </span>
                <span className="font-mono text-base font-semibold" style={{ color: C.gold }}>
                  {Number(balance).toLocaleString(undefined, { maximumFractionDigits: 4 })}
                </span>
              </div>

              <div className="flex items-center gap-2 text-xs" style={{ color: C.muted }}>
                <span className="inline-block h-1.5 w-1.5 rounded-full" style={{ background: C.green }} />
                {dailyLimitReached
                  ? t('epSell.dailyLimitReached')
                  : t('epSell.dailyLimitOk', { epType })}
              </div>
            </div>

            <fieldset disabled={formDisabled} className="contents">
              <ThemedField label={t('epSell.qty')} required>
                <ThemedInput
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  placeholder={`${EP_TRADE_MIN_ORDER_EP} - ${EP_TRADE_MAX_ORDER_EP}`}
                  min={EP_TRADE_MIN_ORDER_EP}
                  max={EP_TRADE_MAX_ORDER_EP}
                  step={1}
                  style={{ color: COLORS.iconGlobal }}
                />
              </ThemedField>

              <ThemedField label={t('epSell.unitPrice')} required>
                <div className="relative flex items-center">
                  <ThemedInput
                    type="number"
                    value={unitPriceUsdt}
                    readOnly
                    placeholder="0.0000"
                    min={0}
                    step="any"
                    style={{ color: COLORS.iconGlobal }}
                  />
                  <span
                    className="pointer-events-none absolute right-3 text-base font-semibold"
                    style={{ color: C.gold }}
                  >
                    USDT
                  </span>
                </div>
              </ThemedField>
              {activeTier ? (
                <p className="text-xs" style={{ color: C.muted }}>
                  {t(
                    'epSell.tierHint',
                    '单价取自 EP 交易配置档位 {{level}}（汇率带 {{min}}-{{max}}）',
                    {
                      level: activeTier.level,
                      min: activeTier.cny_min,
                      max: activeTier.cny_max,
                    },
                  )}
                </p>
              ) : (
                <p className="text-xs" style={{ color: C.red }}>
                  {tierError
                    ? t(
                        'epSell.tierMissing',
                        '未匹配到 EP 交易配置档位，请联系管理员检查价格表',
                      )
                    : t('common.loading', '加载中…')}
                </p>
              )}

              <ThemedField label={t('epSell.paymentMethod')} required>
                <div
                  className="flex h-10 w-full items-center justify-between rounded-lg border px-3 text-base font-semibold"
                  style={{
                    borderColor: 'rgba(245,236,99,0.30)',
                    background: 'rgba(0,0,0,0.35)',
                    color: '#E5E7EB',
                  }}
                >
                  <span>{t('epSell.usdt', 'USDT (BEP20)')}</span>
                  <span className="text-xs font-medium" style={{ color: C.muted }}>
                    {t('epSell.usdtOnly', '仅支持')}
                  </span>
                </div>
              </ThemedField>

              <ThemedField label={t('epSell.paymentNote')}>
                <ThemedTextarea
                  value={paymentNote}
                  onChange={(e) => setPaymentNote(e.target.value)}
                  placeholder={t('epSell.usdtWalletHint')}
                  rows={3}
                  style={{ color: COLORS.iconGlobal }}
                  disabled
                />
              </ThemedField>

              {boundWallet ? (
                <ThemedNotice tone="info">
                  {t('epSell.usdtWalletHint')}: {boundWallet.slice(0, 8)}…{boundWallet.slice(-6)}
                </ThemedNotice>
              ) : (
                <ThemedNotice tone="error">
                  <button
                    type="button"
                    className="underline"
                    onClick={() => navigate('/bind-wallet')}
                  >
                    {t('epSell.usdtWalletRequired')}
                  </button>
                </ThemedNotice>
              )}

              <TransactionSecurity
                txPassword={txPassword}
                onTxPasswordChange={setTxPassword}
                showSecurity={showSecurity}
                onShowSecurityChange={setShowSecurity}
                securityAnswer={securityAnswer}
                onSecurityAnswerChange={setSecurityAnswer}
                securityQuestionText={questionText}
                disabled={formDisabled}
                colors={{
                  muted: C.muted,
                  red: C.red,
                  activeGreen: C.activeGreen,
                  gold: C.gold,
                  softBorder: C.softBorder,
                  inputBg: C.inputBg,
                }}
              />
            </fieldset>
          </ThemedPanel>

          <div className="text-xs" style={{ color: C.muted }}>
            {t('epSell.feeNotice', { epType })}
          </div>

          {estimatedIncomeUsdt !== null && (
            <ThemedPanel className="p-4">
              <div className="space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <span style={{ color: C.muted }}>{t('epSell.preview')}</span>
                  <span className="font-mono font-semibold" style={{ color: C.gold }}>
                    {estimatedIncomeUsdt.toLocaleString(undefined, {
                      maximumFractionDigits: 4,
                    })}{' '}
                    USDT
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span style={{ color: C.muted }}>{t('epSell.lockEP', { epType })}</span>
                  <span className="font-mono" style={{ color: C.red }}>
                    {sellerLockEp != null ? sellerLockEp.toLocaleString() : '—'}
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs" style={{ color: C.muted }}>
                  <span>{t('epSell.listingFeeIncluded', { fee: listingFeeEp })}</span>
                </div>
              </div>
            </ThemedPanel>
          )}

          {error && <ThemedNotice tone="error">{error}</ThemedNotice>}
          {success && <ThemedNotice tone="success">{t('epSell.successMsg')}</ThemedNotice>}

          <Button
            type="button"
            onClick={handleSubmit}
            disabled={formDisabled || !requiredFieldsFilled || !activeTier}
            isLoading={sellMut.isPending}
            fullWidth
            size="lg"
            className="rounded-xl border px-5 text-center text-sm font-medium transition-all hover:brightness-110 disabled:brightness-75"
            style={{
              background: C.buttonBg,
              borderColor: C.buttonBorder,
              boxShadow: C.buttonShadow,
              color: C.text,
            }}
          >
            {t('epSell.submit')}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default EpSellPage;
